// Composables
import { RouteRecordRaw, createRouter, createWebHistory } from 'vue-router'
import NProgress from 'nprogress'

import { useAppStore } from '@/store/app'
import { useUserStore } from '@/store/user'
import { useCatalogStore } from '@/store/catalog'
import { useAppCookies } from '@/composables/useCookies'

import AuthService from '@/services/authService'
import PortalService from '@/services/portalService'
import BusinessProfileService from '@/services/businessProfileService'

import InvalidPortalError from '@/errors/InvalidPortalError'
import InvalidBusinessProfileRequestedError from '@/errors/InvalidBusinessProfileRequestedError'

import { IApp } from '@/types/app'

// Routes
import authRoutes from '@/views/Authentication/router'
import samplePageRoutes from '@/views/Home/router'
import AudienceListingRoute from '@/views/AudienceListing/AudienceListingRoute'
import AudienceCreationRoute from '@/views/AudienceCreation/AudienceCreationRoute'

import productSetRoutes from '@/views/ProductsetListing/router'
import productInsightsRoutes from '@/views/ProductInsignts/router'

import productsetCreationRoutes from '@/views/ProductsetCreation/router'
import dashboardRoutes from '@/views/DashboardListing/router'
import catalogManagerRoutes from '@/views/CatalogManager/router'
import productVariantsRoutes from '@/views/ProductVariants/router'
import sourceCatalogListingRoute from '@/views/SourceCatalogListing/SourceCatalogRouter'
import stopLossConfigurationsRoute from '@/views/StopLossConfiguration/router'

import oAuthRedirectRoutes from '@/views/OAuthRedirect/router'
import signupRoutes from '@/views/Signup/router'
import appConfig from '@/constants/appConfig'
import editAudienceRoute from '@/views/EditAudience/editAudienceRoute'
import creativeAutomationListingRoute from '@/views/CreativeAutomation/router'

// helpers
import { logout } from '@/helpers/authHelper'

import {
  TimeZoneService,
  CurrencyService,
  RoimaticService,
  CatalogService
} from '@/services'
// services
const roimaticService = new RoimaticService()
const catalogService = new CatalogService()

const routes: RouteRecordRaw[] = [
  {
    name: 'health',
    path: '/app/health',
    component: () => import('@/views/HealthCheck/HealthCheck.vue')
  },
  {
    name: 'Default',
    path: '/',
    component: () => import('@/layouts/Default.vue'),
    beforeEnter: async (to, from, next) => {
      const appStore = useAppStore()
      const portalService = new PortalService()
      const portalDomain = import.meta.env.DEV
        ? import.meta.env.VITE_PORTAL_DOMAIN || 'roimatic.adbox.pro'
        : window.location.hostname
      try {
        appStore.setPortal(await portalService.getPortalData(portalDomain))
      } catch (error) {
        if (error instanceof InvalidPortalError) {
          next({ name: 'NotFound' })
          return
        }
      }
      next()
    },
    children: [
      authRoutes,
      // to skip authentication temporarily
      {
        path: 'app/:businessProfileId',
        name: 'App',
        component: () => import('@/layouts/AppLayout.vue'),
        children: [
          samplePageRoutes,
          productSetRoutes,
          ...productsetCreationRoutes,
          ...productsetCreationRoutes,
          productInsightsRoutes,
          dashboardRoutes,
          AudienceListingRoute,
          AudienceCreationRoute,
          editAudienceRoute,
          ...catalogManagerRoutes,
          ...productVariantsRoutes,
          sourceCatalogListingRoute,
          ...stopLossConfigurationsRoute,
          ...creativeAutomationListingRoute
        ],
        beforeEnter: async (to, from, next) => {
          const businessProfileId = Number(to.params.businessProfileId)
          if (isNaN(businessProfileId)) {
            // If a business profile id is not provided, redirect to page not found
            next({ name: 'NotFound' })
            return
          }
          if (to.matched[to.matched.length - 1].name === 'App') {
            // If there is no child route of app that was matched, redirect to template listing page
            next({ name: 'Catalogs', params: { businessProfileId } })
            return
          }
          const appStore = useAppStore()
          const userStore = useUserStore()
          const catalogStore = useCatalogStore()
          if (appStore.businessProfiles.length === 0) {
            const businessProfileService = new BusinessProfileService()
            appStore.setBusinessProfiles(
              await businessProfileService.getUserBusinessProfiles(
                appStore.getPortal?.id,
                userStore.user?.id,
                true
              )
            )
          }

          // check user bp access
          if (
            !appStore.getBusinessProfiles
              .map(bp => bp.id)
              .includes(businessProfileId)
          ) {
            appRouter?.push({ name: 'NotFound' })
            throw new InvalidBusinessProfileRequestedError(
              'User does not have access to the selected business profile.'
            )
          }

          //fetch roimaticConfiguration
          if (
            !appStore.roimaticConfiguration ||
            appStore.getSelectedBusinessProfileId !=
              Number(to.params.businessProfileId)
          ) {
            const roimaticConfigResponse =
              await roimaticService.getRoimaticConfiguration(
                Number(to.params.businessProfileId)
              )
            appStore.setRoimaticConfiguraiton(roimaticConfigResponse)
          }

          const currencyService = new CurrencyService()
          const selectedBusinessProfileId = Number(to.params.businessProfileId)
          const bpChange =
            appStore.getSelectedBusinessProfileId !== selectedBusinessProfileId

          if (bpChange) {
            appStore.setSelectedBusinessProfileId(selectedBusinessProfileId)
          }

          // set bp currency
          if (!appStore.getSelectedBusinessProfileCurrency || bpChange) {
            appStore.setSelectedBusinessProfileCurrency(
              await currencyService.getBusinessProfileCurrency(
                selectedBusinessProfileId,
                appStore.getSelectedBusinessProfile?.currencyId ?? 17
              )
            )
          }

          // set bp timezone
          if (!appStore.selectedBusinessProfileTimezone || bpChange) {
            const timeZoneService = new TimeZoneService()
            const timezones = await timeZoneService.getTimezones()
            const timeZoneId =
              appStore.getSelectedBusinessProfile?.timezoneId || 142
            appStore.setSelectedBusinessProfileTimezone(
              timezones.find(x => x.id === timeZoneId) ?? null
            )
          }

          next()
        }
      }
    ]
  },
  oAuthRedirectRoutes,
  signupRoutes,
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/Error/PageNotFound.vue')
  }
]
let appRouter: ReturnType<typeof createRouter> | null = null

const createAppRouter = (app: IApp) => {
  const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    routes
  })

  router.beforeEach((to, from, next) => {
    NProgress.start()
    if (to.meta.title) {
      document.title = to.meta.title as string
    }
    if (
      to.fullPath != '/app/health' &&
      to.path != '/signup' &&
      to.path != '/oAuthRedirect'
    ) {
      const userStore = useUserStore()
      const { cookies } = useAppCookies()
      const user = cookies.get('user')
      const accessTokenCookie = cookies.get('accessToken')
      const accessToken = accessTokenCookie?.accessToken
      if (!user || !accessToken) {
        logout()
      } else if (userStore.user === null) {
        userStore.setUser(user)
      }
    }

    next()
    NProgress.done()
  })

  appRouter = router
  return router
}

export { appRouter }
export default createAppRouter
